import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Badge, Flex, Text, Box, HStack, Select, Input, Image,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import { camionesApi, UPLOADS_BASE, Camion } from '../../../api/serviciosApi';
import FormularioCamion, { ESTADOS_CAMION, estadoCamionColor } from './FormularioCamion';

const TablaCamiones: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const { isOpen: isOpenEliminar, onOpen: onOpenEliminar, onClose: onCloseEliminar } = useDisclosure();
  const { isOpen: isOpenEstado, onOpen: onOpenEstado, onClose: onCloseEstado } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [data, setData] = useState<Camion[]>([]);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      setData(await camionesApi.list());
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const datosFiltrados = data.filter((item) => {
    const matchEstado = filtroEstado ? item.estadoCamion === filtroEstado : true;
    const matchBusqueda = busqueda
      ? item.numeroEconomico?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.marca?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.placas?.toLowerCase().includes(busqueda.toLowerCase())
      : true;
    return matchEstado && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleVer = async (item: any) => {
    try { setSelectedItem(await camionesApi.get(item.id)); } catch { setSelectedItem(item); }
    onOpenVer();
  };
  const handleEditar = async (item: any) => {
    try { setSelectedItem(await camionesApi.get(item.id)); } catch { setSelectedItem(item); }
    onOpenEditar();
  };
  const handleEliminarClick = (item: any) => { setSelectedItem(item); onOpenEliminar(); };
  const handleEstadoClick = (item: any) => { setSelectedItem(item); setNuevoEstado(item.estadoCamion); onOpenEstado(); };

  const handleGuardado = () => { onCloseNuevo(); onCloseEditar(); fetchData(); };

  const handleEliminarConfirmar = async () => {
    if (!selectedItem) return;
    setProcesando(true);
    try {
      await camionesApi.remove(selectedItem.id);
      toast({ title: 'Camión eliminado', status: 'success', duration: 3000 });
      onCloseEliminar();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleEstadoConfirmar = async () => {
    if (!selectedItem || !nuevoEstado) return;
    setProcesando(true);
    try {
      await camionesApi.cambiarEstado(selectedItem.id, nuevoEstado);
      toast({ title: 'Estado actualizado', status: 'success', duration: 3000 });
      onCloseEstado();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo cambiar el estado.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((i) => ({
      'Número Económico': i.numeroEconomico, 'Placas': i.placas, 'Marca': i.marca,
      'Modelo': i.modelo, 'Año': i.anio, 'Capacidad': i.capacidad, 'Estado': i.estadoCamion,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Camiones');
    XLSX.writeFile(wb, `Camiones_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input placeholder="Buscar por económico, marca o placas..." value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '250px' }} />
          <Select placeholder="Filtrar por estado" value={filtroEstado}
            onChange={(e) => { setFiltroEstado(e.target.value); setCurrentPage(1); }} size="sm" w="180px">
            {ESTADOS_CAMION.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={onOpenNuevo}>Nuevo camión</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">Foto</Th>
              <Th color="white" fontSize="xs">Núm. Económico</Th>
              <Th color="white" fontSize="xs">Marca / Modelo</Th>
              <Th color="white" fontSize="xs">Placas</Th>
              <Th color="white" fontSize="xs">Estado</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={6} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={6} textAlign="center" py={8}><Text color="gray.400">No se encontraron camiones</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td>
                    {item.fotografia ? (
                      <Image src={`${UPLOADS_BASE}${item.fotografia}`} alt={item.numeroEconomico} boxSize="40px" objectFit="cover" borderRadius="md" />
                    ) : (
                      <Box boxSize="40px" bg="gray.100" borderRadius="md" />
                    )}
                  </Td>
                  <Td fontWeight="bold">{item.numeroEconomico}</Td>
                  <Td>{[item.marca, item.modelo].filter(Boolean).join(' ') || '—'}</Td>
                  <Td>{item.placas || '—'}</Td>
                  <Td><Badge colorScheme={estadoCamionColor(item.estadoCamion)} fontSize="xs" px={2} py={0.5} borderRadius="full">{item.estadoCamion}</Badge></Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => handleVer(item)}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => handleEditar(item)}>Editar</Button>
                      <Button size="xs" colorScheme="purple" variant="outline" onClick={() => handleEstadoClick(item)}>Estado</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleEliminarClick(item)}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return <Button key={page} size="xs" bg={currentPage === page ? '#5b182b' : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>;
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Detalle del Camión</ModalHeader>
          <ModalBody py={5}>{selectedItem && <FormularioCamion modo="view" data={selectedItem} />}</ModalBody>
          <ModalFooter><Button onClick={onCloseVer}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Editar Camión</ModalHeader>
          <ModalBody py={5}>{selectedItem && <FormularioCamion modo="edit" data={selectedItem} onGuardado={handleGuardado} onCancelar={onCloseEditar} />}</ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="2xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b">Nuevo Camión</ModalHeader>
          <ModalBody py={5}><FormularioCamion modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} /></ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenEstado} onClose={onCloseEstado} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="#5b182b">Cambiar estado — {selectedItem?.numeroEconomico}</ModalHeader>
          <ModalBody>
            <Select value={nuevoEstado} onChange={(e) => setNuevoEstado(e.target.value)}>
              {ESTADOS_CAMION.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="outline" onClick={onCloseEstado} isDisabled={procesando}>Cancelar</Button>
            <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleEstadoConfirmar} isLoading={procesando}>Guardar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isOpenEliminar} leastDestructiveRef={cancelRef} onClose={onCloseEliminar}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color="#5b182b">Eliminar camión</AlertDialogHeader>
            <AlertDialogBody>¿Eliminar el camión <strong>{selectedItem?.numeroEconomico}</strong>? Esta acción no se puede deshacer.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onCloseEliminar} isDisabled={procesando}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleEliminarConfirmar} ml={3} isLoading={procesando} loadingText="Eliminando...">Eliminar</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaCamiones;
